// <------------------------ Room Objects ------------------------>

import { cineamoApiClient } from '../../apiClient';
import { buildUrlQuery, UrlQueryParameter } from '../../helpers/url-query-helper';
import { CinemaRoomDto } from '../../models/cinema-room/CinemaRoomDto.types';
import { RoomObjectDto } from '../../models/room-object/RoomObjectDto.types';
import { RoomObjectInputDtoTypes } from '../../models/room-object/RoomObjectInputDto.types';
import { ShapeDto } from '../../models/shape/ShapeDto.types';
import { ShapeInputDto } from '../../models/shape/ShapeInputDto';
import { TransformDto } from '../../models/transform/TransformDto.types';
import { TransformInputDto } from '../../models/transform/TransformInputDto';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import { RoomObjectListResponse } from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

import { RoomObjectApiEmbed } from './room-object-api.embed';

type GetRoomObjectsParams = UrlQueryParameter & {
    cinemaRoomId?: CinemaRoomDto['id'];
    embed?: RoomObjectApiEmbed[];
};

export async function getRoomObjects(
    urlParams?: GetRoomObjectsParams
): Promise<ApiResponse<RoomObjectListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<RoomObjectListResponse>>(`/room-objects${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Room Object ------------------------>

export async function getRoomObjectById(id: RoomObjectDto['id']): Promise<ApiResponse<RoomObjectDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<RoomObjectDto>>(`/room-objects/${id}`)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Create Room Object ------------------------>

export async function createRoomObject(
    roomObject: RoomObjectInputDtoTypes
): Promise<ApiResponse<RoomObjectDto> | ApiErrorResponse<RoomObjectInputDtoTypes>> {
    return cineamoApiClient
        .post('/room-objects', roomObject)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Update Room Object ------------------------>

export async function updateRoomObjectById(
    roomObjectId: number,
    roomObject: RoomObjectInputDtoTypes
): Promise<ApiResponse<RoomObjectDto> | ApiErrorResponse<RoomObjectInputDtoTypes>> {
    return cineamoApiClient
        .patch(`/room-objects/${roomObjectId}`, roomObject)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Delete Room Object ------------------------>

export async function deleteRoomObjectById(roomObjectId: number): Promise<boolean> {
    return cineamoApiClient
        .delete(`/room-objects/${roomObjectId}`)
        .then((response) => response.status === 204)
        .catch(() => false);
}

// <------------------------ Set Room Object Shape ------------------------>

export async function setRoomObjectShape(
    roomObjectId: number,
    shape: ShapeInputDto
): Promise<ApiResponse<ShapeDto> | ApiErrorResponse<ShapeInputDto>> {
    return cineamoApiClient
        .put(`/room-objects/${roomObjectId}/shape`, shape)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Set Room Object Transform ------------------------>

export async function setRoomObjectTransform(
    roomObjectId: number,
    transform: TransformInputDto
): Promise<ApiResponse<TransformDto> | ApiErrorResponse<TransformInputDto>> {
    return cineamoApiClient
        .put(`/room-objects/${roomObjectId}/transform`, transform)
        .then((response) => response.data)
        .catch((error) => error);
}
